import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronRight, FileText, Calendar, ArrowLeft, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import FreeSurveyDetail from './FreeSurveyDetail';
import { generateFreeSurveyPDF } from '@/utils/freeSurveyPdfGenerator';
import { useToast } from '@/hooks/use-toast';

export interface FreeSurveyResponse {
  id: string;
  assessmentId: string;
  assessmentTitle: string;
  assessmentSlug: string;
  isMaturityModel: boolean;
  personalInfo: any;
  scoresByCategory: Record<string, { current: number; target: number; categoryName?: string }>;
  maturityScores: Record<string, any>;
  answerDetails: Record<string, any>;
  pdfUrl: string | null;
  submittedAt: string;
  categories?: Array<{
    id: string;
    name: string;
    description: string;
    questions: Array<{
      id: string;
      text: string;
      answerType: string;
      answers: Array<{
        id: string;
        text: string;
        value: number;
        recommendedActions?: Array<{ id?: string; text: string; priority: string }>;
      }>;
    }>;
  }>;
}

interface FreeSurveyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function FreeSurveyDialog({ open, onOpenChange }: FreeSurveyDialogProps) {
  const [responses, setResponses] = useState<FreeSurveyResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState<FreeSurveyResponse | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setSelectedResponse(null);
      // Only fetch if we don't already have data
      if (responses.length === 0) {
        fetchResponses();
      }
    }
  }, [open]);

  // Prefetch on mount so data is ready when dialog opens
  useEffect(() => {
    fetchResponses();
  }, []);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-my-responses');
      if (error) throw error;
      setResponses(data || []);
    } catch (err) {
      console.error('Error fetching responses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (e: React.MouseEvent, response: FreeSurveyResponse) => {
    e.stopPropagation();
    setDownloadingId(response.id);
    try {
      if (response.pdfUrl) {
        window.open(response.pdfUrl, '_blank');
        toast({ title: 'Report opened', description: 'Your assessment report has been opened in a new tab.' });
      } else {
        await generateFreeSurveyPDF(response);
        toast({ title: 'Report downloaded', description: 'Your assessment report has been downloaded.' });
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to download report.' });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedResponse && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSelectedResponse(null)}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {selectedResponse ? selectedResponse.assessmentTitle : 'Submitted Free Surveys'}
          </DialogTitle>
        </DialogHeader>

        {selectedResponse ? (
          <FreeSurveyDetail response={selectedResponse} />
        ) : (
          <div className="space-y-3">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading your surveys...</div>
            ) : responses.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>No free surveys submitted yet.</p>
              </div>
            ) : (
              responses.map((response) => (
                <Card
                  key={response.id}
                  className="cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setSelectedResponse(response)}
                >
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground">{response.assessmentTitle}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {format(new Date(response.submittedAt), 'MMM dd, yyyy')}
                        </span>
                        {response.isMaturityModel && (
                          <Badge variant="secondary" className="text-xs">Maturity Model</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        disabled={downloadingId === response.id}
                        onClick={(e) => handleDownloadReport(e, response)}
                      >
                        {downloadingId === response.id ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Download className="h-4 w-4 mr-1" />
                        )}
                        Download Report
                      </Button>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
